import React, { useState } from 'react';
import { isEqual } from 'lodash';
import { Popover } from 'antd';
import { EyeOutlined } from '@ant-design/icons';

import CustomDimensionRulesRender from './CustomDimensionRulesRender';
import { MappingRulesRender } from '@src/emp-3.0/components/MappedEventDetail';
import TransformRulesRender from './TransformRulesRender';

import {
  StyledArrow,
  StyledCurrentNameText,
  StyledDiffDisplayCard,
  StyledDiffDisplayCardWrapper,
  StyledDivider,
  StyledNoChangeText,
  StyledOriginNameText,
  StyledRelatedNameText,
  StyledTitleText,
  StyledUpdateText,
} from './styled';
import {
  getDisplayValue,
  getOverallDiffValues,
  getValue,
  hasValues,
  isCustomDimensionRelatedMappedEventNames,
  isNotNullOrUndefined,
  isValueEmpty,
} from './utils';
import {
  customDimensionDiffKeys,
  dataTransformDiffKeys,
  deploymentKeysMap,
  DIVIDER,
  flowDiffKeys,
  mappedEventDiffKeys,
  metricDiffKeys,
  relatedDiffKeys,
  rulesDiffKeys,
} from './constants';

const ComparisonSymbol = ({ text = 'to' }) => <StyledArrow>{text}</StyledArrow>;

function renderComparisonSymbol(hasOriginValues, hasCurrentValues) {
  return isNotNullOrUndefined(hasOriginValues) && isNotNullOrUndefined(hasCurrentValues) ? (
    <ComparisonSymbol />
  ) : null;
}

// A way to render overall contrast
// detailText, originValues and currentValues have a maximum of two entries
function renderOverallDiffDisplayCard(detailText: string[], originValues, currentValues) {
  const titleText = detailText[0];
  const hasOriginValues = originValues.length > 0;
  const hasCurrentValues = currentValues.length > 0;

  if (!hasOriginValues && !hasCurrentValues) return null;

  const renderValues = (values) => (
    <React.Fragment>
      {values[0] && `${values[0]} `}
      {detailText?.[1] && `${detailText?.[1]} `}
      {values[1]}
    </React.Fragment>
  );

  return (
    <StyledDiffDisplayCard>
      <StyledTitleText>{titleText}</StyledTitleText>
      {hasOriginValues && <StyledOriginNameText>{renderValues(originValues)}</StyledOriginNameText>}
      {hasOriginValues && hasCurrentValues && renderComparisonSymbol(originValues, currentValues)}
      {hasCurrentValues && (
        <StyledCurrentNameText>{renderValues(currentValues)}</StyledCurrentNameText>
      )}
    </StyledDiffDisplayCard>
  );
}

// A way to render the internal contrast of a sentence (At present, there is no such case, and the subsequent useless can be removed.)
// function renderInlineDiffDisplayCard(
//   detailText: string[],
//   unit: string,
//   originValues,
//   currentValues,
// ) {
//   const hasOriginValues = originValues.length > 0;
//   const hasCurrentValues = currentValues.length > 0;
//
//   if (!hasOriginValues && !hasCurrentValues) return null;
//   const renderValue = (value, Component, unit?) => {
//     return isNotNullOrUndefined(value) ? (
//       <Component>
//         {value}
//         {unit}
//       </Component>
//     ) : null;
//   };
//
//   return (
//     <StyledDiffDisplayCard>
//       <StyledTitleText>
//         {currentValues.map((value, index) => {
//           const originValue = originValues[index];
//           const originDisplayValue = deploymentValuesMap[originValue] ?? originValue;
//           const currentDisplayValue = deploymentValuesMap[value] ?? value;
//
//           return (
//             <React.Fragment key={index}>
//               {detailText[index]}
//               {renderValue(originDisplayValue, StyledOriginNameText, unit)}
//               {renderComparisonSymbol(originValue, value)}
//               {renderValue(currentDisplayValue, StyledCurrentNameText, unit)}
//             </React.Fragment>
//           );
//         })}
//         {detailText.length > currentValues.length ? detailText.at(-1) : null}
//       </StyledTitleText>
//     </StyledDiffDisplayCard>
//   );
// }

const renderContent = (field, current) => {
  const currentValue = getValue(current, field);
  switch (field) {
    case 'mapped_event_names':
      return <CustomDimensionRulesRender current={current} />;
    case 'rules':
      return <MappingRulesRender rules={currentValue} />;
    case 'transform_rule':
      return <TransformRulesRender rules={currentValue} />;
    default:
      return null;
  }
};

const DiffDisplayItem = ({ field, diffKeysMap, data }) => {
  const { origin, current, is_created } = data;

  const [open, setOpen] = useState(false);
  const handleOpenChange = (newOpen: boolean) => setOpen(newOpen);

  if (Array.isArray(field)) {
    const originValues = getOverallDiffValues(origin, field);
    const currentValues = getOverallDiffValues(current, field);

    // Check originValues or currentValues has values
    if (!hasValues(originValues) && !hasValues(currentValues)) {
      return null;
    }

    const fieldMap: { [key: string]: () => ReturnType<typeof renderOverallDiffDisplayCard> } = {
      'within_range,within_unit': () =>
        renderOverallDiffDisplayCard(['Within Range:'], originValues, currentValues),
      'min_value,max_value': () =>
        renderOverallDiffDisplayCard(['Distribution:', '-'], originValues, currentValues),
      'timeout,timeout_unit': () =>
        renderOverallDiffDisplayCard(
          ['Success/Error Time Constraint:'],
          originValues,
          currentValues,
        ),
      'distribution_min,distribution_max': () =>
        renderOverallDiffDisplayCard(
          ['Duration Distribution Range:', '-'],
          originValues,
          currentValues,
        ),
    };

    const fieldKey = field.join(',');

    return fieldMap[fieldKey]?.() ?? null;
  } else {
    const title = diffKeysMap[field] ?? field;
    const originValue = getValue(origin, field);
    const currentValue = getValue(current, field);

    const isOriginValueEmpty = isValueEmpty(originValue);
    const isCurrentValueEmpty = isValueEmpty(currentValue);
    const isRelatedEventName = isCustomDimensionRelatedMappedEventNames(field);

    if (isOriginValueEmpty && isCurrentValueEmpty && !isRelatedEventName) {
      return null;
    }

    // Render rules content
    if (rulesDiffKeys.includes(field)) {
      const isUpdated =
        is_created ||
        (() => {
          if (isCustomDimensionRelatedMappedEventNames(field)) {
            const attributeChanged =
              origin?.attribute_name &&
              current?.attribute_name &&
              !isEqual(origin.attribute_name, current.attribute_name);
            const mappedEventNamesChanged =
              originValue && currentValue && !isEqual(originValue, currentValue);
            return attributeChanged || mappedEventNamesChanged;
          } else {
            return !isValueEmpty(originValue);
          }
        })();

      return (
        <StyledDiffDisplayCard>
          <StyledTitleText>{title} :</StyledTitleText>
          {isUpdated ? (
            <StyledUpdateText>Updated</StyledUpdateText>
          ) : (
            <StyledNoChangeText>No Change</StyledNoChangeText>
          )}
          {
            <Popover
              content={renderContent(field, current)}
              title=""
              trigger="hover"
              placement="rightTop"
              arrow={false}
              open={open}
              onOpenChange={handleOpenChange}
            >
              <EyeOutlined
                style={{
                  fontSize: '20px',
                  marginLeft: '4px',
                  color: open ? '#0046FF' : 'inherit',
                }}
              />
            </Popover>
          }
        </StyledDiffDisplayCard>
      );
    }

    // Render related content
    if (relatedDiffKeys.includes(field)) {
      return (
        <StyledDiffDisplayCard>
          <StyledTitleText>{title} :</StyledTitleText>
          <div style={{ display: 'inline-flex', gap: '8px', flexWrap: 'wrap' }}>
            {currentValue.map((value) => {
              return <StyledRelatedNameText key={value}>{value}</StyledRelatedNameText>;
            })}
          </div>
        </StyledDiffDisplayCard>
      );
    }

    return (
      <StyledDiffDisplayCard>
        <StyledTitleText>{title} :</StyledTitleText>
        {!isValueEmpty(originValue) && (
          <StyledOriginNameText>{getDisplayValue(field, originValue)}</StyledOriginNameText>
        )}
        {!isValueEmpty(originValue) && !isValueEmpty(currentValue) && <ComparisonSymbol />}
        {!isValueEmpty(currentValue) && (
          <StyledCurrentNameText>{getDisplayValue(field, currentValue)}</StyledCurrentNameText>
        )}
      </StyledDiffDisplayCard>
    );
  }
};

const DiffDisplay = ({ displayDiffKeys, diffKeysMap, data }) => {
  const { origin, current } = data;
  const hasContent = (diffKeys, origin, current) =>
    diffKeys.some((keys) => {
      if (Array.isArray(keys)) {
        return (
          keys.every((key) => !isValueEmpty(getValue(origin, key))) ||
          keys.every((key) => !isValueEmpty(getValue(current, key)))
        );
      } else {
        return (
          !isValueEmpty(getValue(origin, keys)) ||
          !isValueEmpty(getValue(current, keys)) ||
          isCustomDimensionRelatedMappedEventNames(keys)
        );
      }
    });

  return (
    <StyledDiffDisplayCardWrapper>
      {displayDiffKeys.map((key, index) => {
        const hasContentBeforeDivider = hasContent(
          displayDiffKeys.slice(0, index),
          origin,
          current,
        );
        const hasContentAfterDivider = hasContent(
          displayDiffKeys.slice(index + 1),
          origin,
          current,
        );
        const shouldRenderDivider =
          key === DIVIDER && hasContentBeforeDivider && hasContentAfterDivider;

        return (
          <React.Fragment key={`${key}_${index}`}>
            {key !== DIVIDER && (
              <DiffDisplayItem diffKeysMap={diffKeysMap} field={key} data={data} />
            )}
            {shouldRenderDivider && <StyledDivider />}
          </React.Fragment>
        );
      })}
    </StyledDiffDisplayCardWrapper>
  );
};

const MetricDiffDisplay = ({ data }) => {
  return (
    <DiffDisplay
      displayDiffKeys={metricDiffKeys}
      diffKeysMap={deploymentKeysMap.metrics}
      data={data}
    />
  );
};

const MappedEventDiffDisplay = ({ data }) => (
  <DiffDisplay
    displayDiffKeys={mappedEventDiffKeys}
    diffKeysMap={deploymentKeysMap.mapped_events}
    data={data}
  />
);

const CustomDimensionDiffDisplay = ({ data }) => {
  return (
    <DiffDisplay
      displayDiffKeys={customDimensionDiffKeys}
      diffKeysMap={deploymentKeysMap.custom_dimension}
      data={data}
    />
  );
};

const DataTransformDiffDisplay = ({ data }) => (
  <DiffDisplay
    displayDiffKeys={dataTransformDiffKeys}
    diffKeysMap={deploymentKeysMap.data_transform}
    data={data}
  />
);

const FlowDiffDisplay = ({ data }) => (
  <DiffDisplay displayDiffKeys={flowDiffKeys} diffKeysMap={deploymentKeysMap.flow} data={data} />
);

export const renderDiffDisplay = (data) => {
  const { type } = data;
  switch (type) {
    case 'metrics':
      return <MetricDiffDisplay data={data} />;
    case 'mapped_events':
      return <MappedEventDiffDisplay data={data} />;
    case 'custom_dimension':
      return <CustomDimensionDiffDisplay data={data} />;
    case 'data_transform':
      return <DataTransformDiffDisplay data={data} />;
    case 'flow':
      return <FlowDiffDisplay data={data} />;
    default:
      return null;
  }
};
